import React, { useState, useCallback, useEffect } from "react";
import { ParticipantPanel } from "./ParticipantPanel";
import { InitiativeTracker } from "./InitiativeTracker";
import { DMToolsPanel } from "./DMToolsPanel";
import { DiceRoller } from "../dice/DiceRoller";
import { DiceRollDisplay } from "./DiceRollDisplay";
import { SessionCharacterPanel } from "./SessionCharacterPanel";
import { FloatingPanel, FloatingPanelState } from "./FloatingPanel";
import { usePanelPreferences } from "../../hooks/usePanelPreferences";
import CampaignWorld from "../../pages/campaigns/view/CampaignWorld";
import { NotesPanel } from "./NotesPanel";
import { KnowledgePanel } from "./KnowledgePanel";
import { AssetCodexPanel } from "./AssetCodexPanel";
import { ChatInterface } from "../chat/ChatInterface";
import { SessionLogPanel } from "../chat/SessionLogPanel";
import { sessionService, SessionParticipant } from "../../services/sessionService";
import { HudTopBar } from "./HudTopBar";
import { HudToolbar, PanelId, PanelMeta } from "./HudToolbar";
import { TetherLines } from "./TetherLines";
import { getTheme, getHud } from "../../config/sessionThemes";
import "./SessionScrollbars.css";

// ─── Default panel states ─────────────────────────────────────────────────────

const DEFAULT_PANEL_STATES: Record<PanelId, FloatingPanelState> = {
  world: { x: 40, y: 60, width: 300, height: 400, minimized: false },
  notes: { x: 40, y: 60, width: 440, height: 500, minimized: false },
  participants: { x: 60, y: 60, width: 280, height: 340, minimized: false },
  initiative: { x: 80, y: 80, width: 300, height: 420, minimized: false },
  character: { x: 100, y: 80, width: 380, height: 560, minimized: false },
  dice: { x: 120, y: 100, width: 300, height: 360, minimized: false },
  tools: { x: 140, y: 100, width: 300, height: 400, minimized: false },
  knowledge: { x: 160, y: 60, width: 320, height: 480, minimized: false },
  codex: { x: 180, y: 60, width: 520, height: 520, minimized: false },
  chat: { x: 60, y: 80, width: 380, height: 480, minimized: false },
  log: { x: 460, y: 80, width: 360, height: 480, minimized: false },
};

const PANEL_META: Record<
  PanelId,
  PanelMeta & { minW: number; minH: number; maxW: number; maxH: number }
> = {
  world: { icon: "🌍", title: "World Assets", minW: 240, minH: 250, maxW: 500, maxH: 700 },
  notes: { icon: "📝", title: "Notes", minW: 320, minH: 360, maxW: 700, maxH: 900 },
  participants: { icon: "👥", title: "Players", minW: 240, minH: 220, maxW: 480, maxH: 600 },
  initiative: { icon: "⚔️", title: "Turn Order", minW: 240, minH: 280, maxW: 500, maxH: 700 },
  character: { icon: "📋", title: "Hero Sheet", minW: 300, minH: 360, maxW: 600, maxH: 900 },
  dice: { icon: "🎲", title: "Dice Roller", minW: 240, minH: 240, maxW: 500, maxH: 600 },
  tools: { icon: "🔧", title: "GM Sanctum", minW: 240, minH: 260, maxW: 500, maxH: 700 },
  knowledge: { icon: "📖", title: "Knowledge", minW: 240, minH: 300, maxW: 600, maxH: 800 },
  codex: { icon: "🗝️", title: "My Entries", minW: 360, minH: 320, maxW: 800, maxH: 900 },
  chat: { icon: "💬", title: "The Hearth", minW: 280, minH: 300, maxW: 600, maxH: 800 },
  log: { icon: "📜", title: "Annals", minW: 260, minH: 280, maxW: 600, maxH: 800 },
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface SessionLayoutProps {
  sessionId: string;
  userId: string;
  characterId?: string;
  isDM: boolean;
  isConnected: boolean;
  reconnectAttempts: number;
  campaignId?: number;
  campaignCreatedBy?: string;
  theme?: string;
  voiceEnabled?: boolean;
  voiceMuted?: boolean;
  voiceVolume?: number;
  onToggleVoice?: () => void;
  onToggleMute?: () => void;
  onVolumeChange?: (_volume: number) => void;
  onOpenAudioSettings?: () => void;
  onLeaveSession?: () => void;
}

export const SessionLayout: React.FC<SessionLayoutProps> = ({
  sessionId,
  userId,
  characterId,
  isDM,
  isConnected,
  reconnectAttempts,
  campaignId,
  campaignCreatedBy,
  theme = "dark",
  voiceEnabled = false,
  voiceMuted = false,
  voiceVolume = 0.7,
  onToggleVoice = () => {},
  onToggleMute = () => {},
  onVolumeChange = () => {},
  onOpenAudioSettings = () => {},
  onLeaveSession,
}) => {
  const currentTheme = getTheme(theme);
  const hud = getHud(currentTheme);

  const [preferences, updatePreference] = usePanelPreferences(userId);
  const [openPanels, setOpenPanels] = useState<Set<PanelId>>(new Set());
  const [focusOrder, setFocusOrder] = useState<PanelId[]>([]);
  const [participants, setParticipants] = useState<SessionParticipant[]>([]);

  useEffect(() => {
    sessionService
      .getSessionParticipants(sessionId)
      .then((r) => setParticipants(r.participants))
      .catch(() => {});
  }, [sessionId]);

  // ── Panel management ─────────────────────────────────────────────────────

  const openPanel = useCallback((id: PanelId) => {
    setOpenPanels((prev) => new Set([...prev, id]));
    setFocusOrder((prev) => [...prev.filter((p) => p !== id), id]);
  }, []);

  const closePanel = useCallback((id: PanelId) => {
    setOpenPanels((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
    setFocusOrder((prev) => prev.filter((p) => p !== id));
  }, []);

  const togglePanel = useCallback(
    (id: PanelId) => (openPanels.has(id) ? closePanel(id) : openPanel(id)),
    [openPanels, openPanel, closePanel]
  );

  const focusPanel = useCallback((id: PanelId) => {
    setFocusOrder((prev) => {
      if (prev[prev.length - 1] === id) return prev;
      return [...prev.filter((p) => p !== id), id];
    });
  }, []);

  const getZIndex = useCallback((id: PanelId) => 50 + focusOrder.indexOf(id), [focusOrder]);

  // ── Toolbar panel list (player vs DM) ────────────────────────────────────

  const toolbarPanels = (
    isDM
      ? ([
          // "world",
          "notes",
          "participants",
          "initiative",
          "character",
          "dice",
          // "tools",
          "knowledge",
          "codex",
          "chat",
          "log",
        ] as PanelId[])
      : ([
          // "world",
          "notes",
          "participants",
          "initiative",
          "character",
          "dice",
          "knowledge",
          "codex",
          "chat",
          "log",
        ] as PanelId[])
  ).map((id) => ({ id, meta: { icon: PANEL_META[id].icon, title: PANEL_META[id].title } }));

  // ── Panel content ─────────────────────────────────────────────────────────

  const renderPanelContent = (id: PanelId) => {
    switch (id) {
      case "world":
        return campaignId ? (
          <CampaignWorld campaignId={campaignId} />
        ) : (
          <div className="text-zinc-400 text-sm p-4">
            <p>Campaign world tree will appear here.</p>
            <p className="mt-2 text-zinc-500">Shows only assets shared with your character.</p>
          </div>
        );
      case "notes":
        return (
          <NotesPanel
            sessionId={sessionId}
            isDM={isDM}
            campaignId={campaignId}
            theme={theme}
            className="h-full"
          />
        );
      case "participants":
        return <ParticipantPanel sessionId={sessionId} className="h-full" />;
      case "initiative":
        return <InitiativeTracker className="h-full" sessionId={sessionId} isDM={isDM} />;
      case "character":
        return (
          <SessionCharacterPanel
            characterId={characterId}
            sessionId={sessionId}
            className="h-full"
          />
        );
      case "dice":
        return (
          <DiceRoller
            sessionId={sessionId}
            characterId={characterId}
            defaultPrivate={false}
            className="h-full"
          />
        );
      case "tools":
        return isDM ? <DMToolsPanel isDM={isDM} className="h-full" /> : null;
      case "knowledge":
        return (
          <KnowledgePanel
            campaignId={campaignId}
            campaignCreatedBy={campaignCreatedBy}
            className="h-full"
          />
        );
      case "codex":
        return (
          <AssetCodexPanel
            campaignId={campaignId}
            sessionId={sessionId}
            participants={participants}
            className="h-full"
          />
        );
      case "chat":
        return (
          <ChatInterface
            maxHeight="100%"
            showUserJoinLeave={true}
            sessionId={sessionId}
            participants={participants}
            currentUserId={userId}
            className="h-full"
          />
        );
      case "log":
        return <SessionLogPanel sessionId={sessionId} maxHeight="100%" className="h-full" />;
      default:
        return null;
    }
  };

  // ── Board background ──────────────────────────────────────────────────────

  const boardBg = [
    `radial-gradient(circle at 28% 38%, ${hud.boardGlow1} 0%, transparent 50%)`,
    `radial-gradient(circle at 76% 64%, ${hud.boardGlow2} 0%, transparent 42%)`,
    `linear-gradient(135deg, ${currentTheme.colors.background} 0%, ${currentTheme.colors.backgroundSecondary} 100%)`,
  ].join(", ");

  const gridOverlay = [
    `linear-gradient(${hud.boardGridColor} 1px, transparent 1px)`,
    `linear-gradient(90deg, ${hud.boardGridColor} 1px, transparent 1px)`,
  ].join(", ");

  // ── Tether configs ────────────────────────────────────────────────────────

  const tetherConfigs = toolbarPanels.map(({ id }) => ({ panelId: id, toolbarId: id }));

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div
      data-testid="session-layout"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        backgroundColor: currentTheme.colors.background,
      }}
    >
      {/* ── Board background (grid + glow) ─────────────────────────────── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: boardBg,
          backgroundImage: gridOverlay,
          backgroundSize: "100% 100%, 48px 48px, 48px 48px",
          backgroundAttachment: "local",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* ── Main game area ─────────────────────────────────────────────── */}
      <div
        data-testid="game-board"
        role="main"
        aria-label="Main game area"
        style={{ position: "absolute", inset: 0, zIndex: 1 }}
      >
        {/* Dice roll overlay */}
        <DiceRollDisplay sessionId={sessionId} userId={userId} />

        {/* Tether lines (SVG, redraws every RAF) */}
        <TetherLines
          configs={tetherConfigs}
          openPanelIds={[...openPanels]}
          strokeColor={hud.tetherStroke}
          dotColor={hud.tetherDot}
        />

        {/* Empty-state hint */}
        {openPanels.size === 0 && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: currentTheme.colors.textMuted,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 72, marginBottom: 12 }}>🗺️</div>
              <h2
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: currentTheme.colors.textPrimary,
                  marginBottom: 8,
                }}
              >
                Game Board
              </h2>
              <p style={{ fontSize: 14 }}>Use the toolbar buttons to open panels</p>
            </div>
          </div>
        )}

        {/* Floating panels */}
        {[...openPanels].map((id) => {
          const meta = PANEL_META[id];
          return (
            <FloatingPanel
              key={id}
              panelId={id}
              title={meta.title}
              icon={meta.icon}
              defaultState={DEFAULT_PANEL_STATES[id]}
              savedState={preferences[id]}
              minWidth={meta.minW}
              minHeight={meta.minH}
              maxWidth={meta.maxW}
              maxHeight={meta.maxH}
              zIndex={getZIndex(id)}
              onClose={() => closePanel(id)}
              onFocus={() => focusPanel(id)}
              onStateChange={(state) => updatePreference(id, state)}
              hud={hud}
            >
              {renderPanelContent(id)}
            </FloatingPanel>
          );
        })}
      </div>

      {/* ── HUD top bar (gradient overlay) ─────────────────────────────── */}
      <HudTopBar
        sessionId={sessionId}
        isDM={isDM}
        isConnected={isConnected}
        reconnectAttempts={reconnectAttempts}
        theme={currentTheme}
        voiceEnabled={voiceEnabled}
        voiceMuted={voiceMuted}
        voiceVolume={voiceVolume}
        onToggleVoice={onToggleVoice}
        onToggleMute={onToggleMute}
        onVolumeChange={onVolumeChange}
        onOpenAudioSettings={onOpenAudioSettings}
      />

      {/* ── HUD toolbar (bottom floating pill) ─────────────────────────── */}
      <HudToolbar
        sessionId={sessionId}
        userId={userId}
        isDM={isDM}
        theme={currentTheme}
        panels={toolbarPanels}
        openPanels={openPanels}
        onTogglePanel={togglePanel}
        onLeaveSession={onLeaveSession}
      />
    </div>
  );
};
